import { Layout, Button, Space, Grid, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from '@store/store';
import {
  savePage,
  selectEditor,
  getGrapesEditor,
} from '../../features/cms/state/editorSlice.ts';
import { previewUrl } from '../../features/cms/api/cmsPages.ts';
import {
  SaveOutlined,
  UndoOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useCmsAccess } from '@features/cms/hooks/useCmsAccess.ts';
import { ensureCssString } from '@features/cms/grapes/utils.tsx';
import { extractCmsImageIds } from '@features/cms/utils/assets.tsx';
import ThemeToggle from './ThemeToggle';
import StaffPublicTopbar, {
  type StaffQuickLink,
} from './cms/StaffPublicTopbar.tsx';
import { CmsLegacySiteSettingsContext, CmsLegacyStaffContext, type CmsLegacyStaffContextValue } from './cms/CmsLegacyStaffContext.tsx';
export { useCmsLegacyStaff } from './cms/CmsLegacyStaffContext.tsx';
import { selectProfile, selectProfileStatus, fetchProfile } from '@store/slices/userSlice.tsx';
import { selectSiteApps, selectSiteSettings } from '@store/slices/settingsSlice.tsx';

const { Content } = Layout;

interface CmsToolbarContextType {
  setRight: (content: React.ReactNode) => void;
}

const CmsToolbarContext = createContext<CmsToolbarContextType>({
  setRight: () => { },
});

export const useCmsToolbar = () => useContext(CmsToolbarContext);

// Layout minimalista para el CMS público
const CmsLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const isLegacySiteBuilder = pathname.startsWith('/site-builder');
  const isModernSiteBuilder = pathname.startsWith('/cms/grapesjs');
  const dispatch = useAppDispatch();
  const { id, slug, title, isDirty, isSaving, status } = useAppSelector(selectEditor);
  const [rightContent, setRightContent] = useState<React.ReactNode>(null);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const cmsAccess = useCmsAccess();
  const profile = useAppSelector(selectProfile);
  const profileStatus = useAppSelector(selectProfileStatus);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const authStatus = useAppSelector((state) => state.auth.status);
  const siteSettings = useAppSelector(selectSiteSettings);
  const apps =
    useAppSelector((state) => selectSiteApps({ settings: state.settings })) ?? [];
  const modernEditMode = isModernSiteBuilder && cmsAccess.status === 'allowed';
  const [legacyEditMode, setLegacyEditMode] = useState(() => (
    typeof window !== 'undefined' && window.sessionStorage.getItem('legacyWebEditMode') === 'true'
  ));
  const isLegacyPublicSurface = pathname.startsWith('/web');
  const explicitLegacyEditMode = new URLSearchParams(location.search).get('editMode') === 'true'
    || location.hash === '#edit';
  const legacyPublicEditMode = isLegacyPublicSurface
    && cmsAccess.status === 'allowed'
    && (legacyEditMode || explicitLegacyEditMode);
  const showStaffTopbar =
    !modernEditMode && !isLegacySiteBuilder && cmsAccess.isStaff && cmsAccess.status !== 'forbidden_role';
  const showFixedStaffTopbar = showStaffTopbar && !isLegacyPublicSurface;

  useEffect(() => {
    const syncLegacyEditMode = () => {
      setLegacyEditMode(window.sessionStorage.getItem('legacyWebEditMode') === 'true');
    };
    window.addEventListener('storage', syncLegacyEditMode);
    window.addEventListener('focus', syncLegacyEditMode);
    syncLegacyEditMode();
    return () => {
      window.removeEventListener('storage', syncLegacyEditMode);
      window.removeEventListener('focus', syncLegacyEditMode);
    };
  }, [location.search, location.hash]);

  useEffect(() => {
    if (isAuthenticated && authStatus === 'idle' && !profile && profileStatus === 'idle') {
      dispatch(fetchProfile());
    }
  }, [authStatus, dispatch, isAuthenticated, profile, profileStatus]);

  const quickLinks = useMemo<StaffQuickLink[]>(() => {
    const links: StaffQuickLink[] = [
      { key: 'dashboard', label: 'Dashboard', to: '/dashboard' },
    ];
    if (apps.includes('eshop')) {
      links.push({ key: 'eshop', label: 'E-shop', to: '/eshop/catalogue' });
    }
    if (apps.includes('erp')) {
      links.push({ key: 'erp', label: 'ERP', to: '/erp' });
    }
    return links;
  }, [apps]);

  const handleEnterEditMode = useCallback(() => {
    window.sessionStorage.setItem('legacyWebEditMode', 'true');
    setLegacyEditMode(true);
    const params = new URLSearchParams(location.search);
    params.set('editMode', 'true');
    const query = params.toString();
    navigate(`${pathname}${query ? `?${query}` : ''}${location.hash}`);
  }, [location.hash, location.search, navigate, pathname]);

  const handleExitEditMode = useCallback(() => {
    window.sessionStorage.removeItem('legacyWebEditMode');
    setLegacyEditMode(false);
    const params = new URLSearchParams(location.search);
    params.delete('editMode');
    const query = params.toString();
    const hash = location.hash === '#edit' ? '' : location.hash;
    navigate(`${pathname}${query ? `?${query}` : ''}${hash}`);
  }, [location.hash, location.search, navigate, pathname]);

  const legacyStaffContext = useMemo<CmsLegacyStaffContextValue | null>(() => {
    if (!showStaffTopbar) return null;
    return {
      cmsAccess,
      profile: profile ?? null,
      quickLinks,
      siteSettings: siteSettings ?? null,
      userMenu: (
        <StaffPublicTopbar
          cmsAccess={cmsAccess}
          profile={profile ?? null}
          quickLinks={quickLinks}
          onEnterEditMode={handleEnterEditMode}
          onExitEditMode={handleExitEditMode}
          editMode={legacyPublicEditMode}
          variant="inline"
        />
      ),
      onEnterEditMode: handleEnterEditMode,
      onExitEditMode: handleExitEditMode,
      editMode: legacyPublicEditMode,
    };
  }, [cmsAccess, handleEnterEditMode, handleExitEditMode, legacyPublicEditMode, profile, quickLinks, showStaffTopbar, siteSettings]);

  const staffTopbarOffset = 0;
  const contentOffset = (modernEditMode ? 50 : 0) + staffTopbarOffset;

  const canSave = modernEditMode && slug && title && isDirty && !isSaving;

  const handleSave = () => {
    dispatch(savePage());
  };

  const handleUndo = () => {
    const editor = getGrapesEditor();
    editor?.runCommand('core:undo', {});
  };

  const handlePreview = () => {
    if (!slug) return;
    void (async () => {
      try {
        const editor = getGrapesEditor();
        if (!editor) {
          message.error('El editor aún no está listo para previsualizar');
          return;
        }
        const html = editor.getHtml();
        const url = await previewUrl({
          slug,
          title: title || slug,
          html,
          css: ensureCssString(editor.getCss()),
          js: editor.getJs(),
          isPublished: status === 'published',
          pageId: id,
          imageIds: extractCmsImageIds(html),
        });
        window.open(url, '_blank', 'noopener');
      } catch (err) {
        message.error('No se pudo generar la vista previa');
      }
    })();
  };

  return (
    <CmsToolbarContext.Provider value={{ setRight: setRightContent }}>
      <Layout style={{ minHeight: '100vh' }}>
        {showFixedStaffTopbar && (
          <StaffPublicTopbar
            cmsAccess={cmsAccess}
            profile={profile ?? null}
            quickLinks={quickLinks}
            onEnterEditMode={handleEnterEditMode}
            onExitEditMode={handleExitEditMode}
            editMode={legacyPublicEditMode}
            // themeToggle={<ThemeToggle size="small" />}
          />
        )}
        {modernEditMode && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              background: '#463a3c',
              // borderBottom: '1px solid #ddd',
              padding: 8,
              zIndex: 1000,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Space>
                <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={!canSave}
                  loading={isSaving}
                  icon={<SaveOutlined />}
                >
                  {!isMobile && 'Guardar'}
                </Button>
                <Button onClick={handleUndo} icon={<UndoOutlined />}>
                  {!isMobile && 'Deshacer'}
                </Button>
                <Button
                  onClick={handlePreview}
                  disabled={!slug}
                  icon={<EyeOutlined />}
                >
                  {!isMobile && 'Vista previa'}
                </Button>
              </Space>
              <Space align="center" size={8} wrap>
                {rightContent}
                <ThemeToggle size={isMobile ? 'small' : 'middle'} withLabel={!isMobile} />
              </Space>
            </div>
          </div>
        )}
        {/* La web legacy no ofrece diseño dual light/dark todavía. El toggle
            queda documentado como mejora futura y se mantiene en el resto de
            superficies del producto. */}
        <CmsLegacySiteSettingsContext.Provider value={siteSettings ?? null}>
          <CmsLegacyStaffContext.Provider value={legacyStaffContext}>
            <Content style={{ marginTop: contentOffset }}>{children}</Content>
          </CmsLegacyStaffContext.Provider>
        </CmsLegacySiteSettingsContext.Provider>
      </Layout>
    </CmsToolbarContext.Provider>
  );
};

export default CmsLayout;
